import React, { useContext, useEffect, useState } from 'react';
import {
  Field,
  RichText,
  SitecoreContext,
  Placeholder,
  ComponentRendering,
  ComponentParams,
  ComponentFactory,
  WithSitecoreContextProps,
} from '@sitecore-jss/sitecore-jss-react';
import './index.scss';
import '../../../assets/styles/_buttons.scss';
import Button from '@mui/material/Button';
import { Navigate } from 'react-router-dom';
import CustomTooltip from '../../CustomTooltip/CustomTooltip';
import { getAPICall } from '../../../dataFetcher/axiosMethodCalls';
import { getCurrencyInUSD } from '../../../utils/helper';
import { APIWebstoreContext } from '../../../context/globalContext';
import LoadingSkeleton from '../../LoadingSkeleton/LoadingSkeleton';
import { CartAnalyticsInfo, checkoutEvent } from '../../../utils/analyticsHelper';
import { UnifiedCartDataService } from '../../../utils/UnifiedCartDataService';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LOB_LIST } from '../../../context/reducer';
import { OrderSummaryLOBFieldProps } from '../OrderSummaryCheckout/OrderSummaryCheckout';
import { CartItemsDetails } from '../../Product/StandardProductCartDetails/StandardProductCartDetails';
import ToastNotification from '../../ToastNotification/ToastNotification';

export type OrderSummaryProps = WithSitecoreContextProps & {
  fields: {
    MemberDiscountTooltipText: Field<string>;
    SubscriptionText: Field<string>;
    SingleStandards: Field<number>;
    MultipleStandards: Field<number>;
    NavigateTo: Field<string>;
    ToastMessage: Field<string>;
    LOBSpecificConfiguration: Array<OrderSummaryLOBFieldProps>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
};

interface Props {
  children: React.ReactElement;
  componentFactory: ComponentFactory;
  rendering: ComponentRendering;
  params: ComponentParams;
}

export interface OrderSummaryDetails {
  orderId: string;
  subTotal: number;
  memberDiscount: number;
  total: number;
  taxTotal: number;
  shippingTotal: number;
}

export interface IOrderSummaryConfigurationInfo {
  ShowAccordion: boolean;
  ShowShipping: boolean;
  ShowSalesTax: boolean;
  ShowMemberDiscount: boolean;
}

export const OrderSummary = (props: OrderSummaryProps & Props): React.JSX.Element => {
  const { fields, rendering } = props;
  const { state } = useContext(APIWebstoreContext);
  const [orderSummaryDetails, setOrderSummaryDetails] = useState<{
    [lob: string]: OrderSummaryDetails;
  }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedLob, setExpandedLob] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [toastMessage, setToastMessage] = useState<Field<string>>();
  const [customToastMessage] = useState(props.fields.ToastMessage);
  const [showToast, setShowToast] = useState(false);
  const [shouldRedirectToCheckout, setShouldRedirectToCheckout] = useState(false);

  const cartLobs = Object.keys(state?.cartCounts?.lobCounts || {}).filter(
    (lob) => (state?.cartCounts?.lobCounts?.[lob] ?? 0) > 0
  );

  function getLOBConfig(lob: string): OrderSummaryLOBFieldProps | undefined {
    return fields.LOBSpecificConfiguration.find((x) => x.fields.LOBIdentifier.value === lob);
  }

  function getOrderSummaryConfiguration(lob: string): IOrderSummaryConfigurationInfo | undefined {
    const lobSpecificConfig = getLOBConfig(lob);
    if (!lobSpecificConfig) return undefined;

    let OrderSummaryConfig = {
      ShowAccordion: lobSpecificConfig?.fields?.ShowAccordion?.value,
      ShowShipping: lobSpecificConfig?.fields?.ShowShipping?.value,
      ShowSalesTax: lobSpecificConfig?.fields?.ShowSalesTax?.value,
      ShowMemberDiscount: lobSpecificConfig.fields?.ShowMemberDiscount?.value,
    };

    return OrderSummaryConfig;
  }

  const fetchData = async () => {
    const orderSummaryDetails: { [lob: string]: OrderSummaryDetails } = {};

    let total = 0;

    for (const lob of LOB_LIST) {
      if (state?.cartCounts?.lobCounts?.[lob] ?? 0 > 0) {
        try {
          const response: any = await getAPICall(
            `${process.env.REACT_APP_CART_URL}/cartOrder/ordersummary?lob=${lob}`
          );

          if (response && response.data) {
            orderSummaryDetails[lob] = response.data;
            total += response.data.total;
          }
        } catch (error) {
          console.error(`Error fetching order summary for ${lob}:`, error);
        }
      }
    }

    setOrderSummaryDetails(orderSummaryDetails);
    setTotal(total);

    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [state?.toggleOrderSummaryLoad, state?.cartCounts]);

  useEffect(() => {
    if (state?.syncFailedCartLOBs?.length) {
      setShouldRedirectToCheckout(false);
    }
  }, [state?.syncFailedCartLOBs]);

  const showCustomToast = (message: string) => {
    customToastMessage.value = message;
    setToastMessage(customToastMessage);
    setShowToast(true);
  };

  const toastRemove = () => {
    setShowToast(false);
  };

  function handleCheckout() {
    formJSONAndPushToGTM();

    if (state?.syncFailedCartLOBs?.length) {
      const syncFailedCartLOBs = state.syncFailedCartLOBs.join(', ');

      showCustomToast(
        state?.sitecoreDictionaryData?.SyncOrderFailureMessage?.replace(
          '##LOB##',
          syncFailedCartLOBs
        ) ||
          `It looks like the ${syncFailedCartLOBs} order(s) may have changed. Please remove and re-add the items to proceed.`
      );

      setShouldRedirectToCheckout(false);
      return;
    }

    setShouldRedirectToCheckout(true);
  }

  function formJSONAndPushToGTM() {
    const unifiedCart = UnifiedCartDataService.getCartInfo();

    Object.keys(orderSummaryDetails).forEach((lob) => {
      const cartInfo: Array<CartItemsDetails> = unifiedCart[lob]?.cartInfo ?? [];
      const orderSummaryLOBDetails = orderSummaryDetails[lob];

      if (cartInfo?.length && orderSummaryLOBDetails) {
        let value: any;
        value = orderSummaryLOBDetails?.total.toFixed(2);

        const itemAnalyticsInfo: CartAnalyticsInfo = {
          currency: 'USD',
          value: value,
          items:
            cartInfo &&
            cartInfo.map((item: CartItemsDetails) => {
              let price: any = item?.priceOption?.unitPrice;
              price = price.toFixed(2);
              return {
                item_id: `${item?.product?.productId}`,
                item_name: `${item?.product?.title}`,
                item_brand: `${item?.product?.productType}`,
                item_variant: item?.priceOption?.variantId,
                price: price,
                quantity: item?.priceOption?.quantity,
              };
            }),
        };

        checkoutEvent(itemAnalyticsInfo);
      }
    });
  }

  function renderWithLoading(value: any, theme?: any, loaderWidth?: any, loaderHeight?: any) {
    return isLoading ? (
      <LoadingSkeleton theme={theme} loaderWidth={loaderWidth} loaderHeight={loaderHeight} />
    ) : (
      value
    );
  }

  function getSubscriptionBox() {
    const singleStandardsCheck =
      Number(state?.maxCartItemQuantity) >= fields?.SingleStandards?.value;
    const differentStandardsCheck =
      Number(state?.cartCounts?.lobCounts.ECOMM) >= fields?.MultipleStandards?.value;
    if (singleStandardsCheck || differentStandardsCheck) {
      return (
        <div className="optInDiv">
          <div className="subscriptionNotifications">
            <label className="subscriptionNotificationText">
              <p className="subscriptionHeading">Subscriptions</p>
              <RichText className="description" field={fields?.SubscriptionText} />
            </label>
            <br></br>
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  }

  function renderOrderSummaryRow(
    label: string,
    amount: number | string,
    mainClass = 'orderSubTotal'
  ) {
    const displayValue =
      typeof amount === 'number'
        ? renderWithLoading(getCurrencyInUSD(amount), 'dark', '90px', '25px')
        : amount;

    return (
      <div className={mainClass}>
        <p className={`${mainClass}Detail`}>{label}</p>
        <p className={`${mainClass}Detail`} style={{ textAlign: 'right' }}>
          {displayValue}
        </p>
      </div>
    );
  }

  function getOrderSummary() {
    if ((state?.cartCounts?.totalCount ?? 0) > 0) {
      return (
        <div className="orderSummary">
          <h1>Order Summary</h1>
          {cartLobs.map((lob) => {
            const lobConfig = fields?.LOBSpecificConfiguration?.find(
              (x) => x.fields.LOBIdentifier.value === lob
            );

            const isExpanded = expandedLob === lob;

            const orderSummaryConfig = getOrderSummaryConfiguration(lob);

            return (
              <React.Fragment key={lob}>
                {orderSummaryConfig?.ShowAccordion ? (
                  <Accordion
                    className="order-summary-accordion"
                    expanded={isExpanded}
                    onChange={() => setExpandedLob((prev) => (prev === lob ? null : lob))}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      id={`${lob}-header`}
                      className="order-summary-standards-collapsed"
                    >
                      <div className="accordionHeader">
                        <p className="accordion-title">{lobConfig?.fields?.SectionTitle?.value}</p>
                        {!isExpanded && (
                          <div className="orderPriceDetails">
                            {renderOrderSummaryRow(
                              'Product Total:',
                              orderSummaryDetails?.[lob]?.subTotal
                            )}
                          </div>
                        )}
                      </div>
                    </AccordionSummary>
                    <hr className="accordion-line" />
                    <AccordionDetails className="accordion-details order-summary-standards-expanded">
                      <div className="orderPriceDetails">
                        {renderOrderSummaryRow(
                          'Product Subtotal:',
                          orderSummaryDetails?.[lob]?.subTotal
                        )}
                        {orderSummaryConfig?.ShowMemberDiscount && (
                          <div className="orderMemberDiscount orderSummaryAlign">
                            <div className="orderMemberDiscountTitleSection">
                              <p>Member Discount (30%):</p>
                              <CustomTooltip
                                fields={{ 'Tooltip Text': fields?.MemberDiscountTooltipText }}
                              />
                            </div>
                            <p className="member-discount-value">
                              {renderWithLoading(
                                getCurrencyInUSD(orderSummaryDetails?.[lob]?.memberDiscount),
                                'light'
                              )}
                            </p>
                          </div>
                        )}
                        {orderSummaryConfig?.ShowShipping &&
                          renderOrderSummaryRow(
                            'Shipping and Handling:',
                            'Calculated at Checkout',
                            'orderShippingAndHandling'
                          )}
                        {orderSummaryConfig?.ShowSalesTax &&
                          renderOrderSummaryRow(
                            'Sales Tax:',
                            'Calculated at checkout',
                            'orderSalesTax'
                          )}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <Accordion className="order-summary-accordion" expanded={false}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon style={{ visibility: 'hidden' }} />}
                      className="order-summary-standards-collapsed "
                    >
                      <div className="accordionHeader">
                        <p className="accordion-title">{lobConfig?.fields?.SectionTitle?.value}</p>
                        <div className="orderPriceDetails">
                          {renderOrderSummaryRow(
                            'Product Total:',
                            orderSummaryDetails?.[lob]?.total
                          )}
                        </div>
                      </div>
                    </AccordionSummary>
                  </Accordion>
                )}
              </React.Fragment>
            );
          })}

          <div className="total-section">
            <div className="orderTotal justify-space-between">
              <div className="orderTotalSection  justify-space-between">
                <p className="orderTotalSection">Total:</p>
                <p>
                  {renderWithLoading(
                    orderSummaryDetails && getCurrencyInUSD(total),
                    'dark',
                    '90px',
                    '25px'
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="orderButtonSection">
            {
              <Button
                id="Checkout"
                className="ws-white-button checkoutButton"
                variant="contained"
                onClick={handleCheckout}
              >
                CHECKOUT
              </Button>
            }
          </div>

          {shouldRedirectToCheckout && <Navigate to="/orders/checkout" />}

          <ToastNotification
            fields={{
              ToastMessage: toastMessage,
            }}
            showToast={showToast}
            removeToast={toastRemove}
            toastStatus="error"
          ></ToastNotification>

          <Placeholder name="continue_shopping" rendering={rendering} isCentered={true} />

          {getSubscriptionBox()}
          <br />
        </div>
      );
    } else {
      return <div className="orderSummary"></div>;
    }
  }
  return <div className="orderSummarySticky">{getOrderSummary()}</div>;
};

export default OrderSummary;
